import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { Actions } from '@ngrx/effects';
import { Observable, Subject } from 'rxjs';
import { ABTestConfig, ACTIONS } from '../../models';
import { fieldsConfig, liveRateMethodTypes, ShippingMethod, Shippings } from '@pages/checkout-app/types';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { ABTestDTO } from '@pages/checkout-app/components/abtest/dialogs/abtest-dialog/abtest.dto';
import { CurrencySign } from '@pdqdev/pdq-shared/dist/entities';
import { ShopService } from '@services/shops/shop.service';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { currencyStrToSign } from '@utils/string-utils';
import { ToastService } from '@storybook/services/toast/toast.service';
import { ABTestGroupDTO } from './abtest-group.dto';
import { customDeliveryPromiseValidator, DisplayRatesAs, VARIANT } from '@pages/checkout-app/configs';

@Component({
  selector: 'abtest-dialog',
  templateUrl: './abtest-dialog.component.html',
  styleUrls: ['./abtest-dialog.component.scss'],
  standalone: false,
})
export class AbtestDialogComponent implements OnInit, OnDestroy {
  @onDestroyComplete() destroyed$ = new Subject();
  // store
  settings$ = this.store.select(fromCheckoutReducer.selectSettings);
  isLoading$ = this.store.select(fromCheckoutReducer.selectIsLoading);
  isABTestLoading$ = this.store.select(fromCheckoutReducer.selectIsABTestLoading);
  activeCheckout$: Observable<Shippings> = this.store.select(fromCheckoutReducer.selectActiveCheckout);

  readonly VARIANT = VARIANT;
  readonly ACTIONS = ACTIONS;
  readonly displayRatesAsConfig = [...DisplayRatesAs];
  readonly CHECKOUT_SETTINGS_URL = '/settings/checkout';

  currencySymbol: CurrencySign = '$';
  originalMethod;
  testVariant;
  category: ShippingMethod;
  mode: ACTIONS;
  currentTab: VARIANT = VARIANT.TEST;
  loadLiveRateForm;
  // form
  formConfig;
  formGroup: UntypedFormGroup;
  testVariantFields;
  selectedAbTestGroup?: ABTestGroupDTO;
  isPreviewOnly = false;
  editRunningABTestData = {
    control: {},
    test: {},
  };

  constructor(
    public dialogRef: MatDialogRef<AbtestDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<fromCheckoutReducer.State>,
    private actions$: Actions,
    private formBuilder: UntypedFormBuilder,
    private shopService: ShopService,
    private router: Router,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    this.isPreviewOnly = this.data.isPreviewOnly;
    this.initProps(this.data);
  }

  get additionalPriceRangeFormArray() {
    return this.formGroup.get('additionalPriceRanges') as UntypedFormArray;
  }

  initProps(data) {
    this.activeCheckout$.pipe(takeUntil(this.destroyed$)).subscribe((checkout) => {
      if (!checkout) {
        return;
      }
      this.originalMethod = checkout ?? null;
      this.category = checkout?.shippingMethod?.category;
      this.loadLiveRateForm = liveRateMethodTypes.includes(this.category);
      this.loadTargetConfig(this.category);
      this.currencySymbol = checkout?.methodCurrency
        ? currencyStrToSign(checkout.methodCurrency)
        : this.shopService.shopCurrencySymbol;
      this.mode = data.mode;
      this.testVariant = data.testVariant ?? null;
      if (data.testVariant?.testVariantFields?.abtestGroupId) {
        this.selectedAbTestGroup = new ABTestGroupDTO();
        this.selectedAbTestGroup.id = data.testVariant.testVariantFields.abtestGroupId;
      }
      this.presetInitialData(this.mode);
    });
  }

  presetInitialData(mode: ACTIONS) {
    switch (mode) {
      case ACTIONS.CREATE:
        // inherit control values on create
        const variant = new ABTestDTO(this.originalMethod, {});
        const variantFieldsDTO = variant.createVariantDTO();
        this.initFormValues(variantFieldsDTO);
        break;
      case ACTIONS.EDIT:
      case ACTIONS.DUPLICATE:
        const testVariantFields = this.testVariant.testVariantFields;
        this.initFormValues(testVariantFields);
        break;
      case ACTIONS.EDIT_RUNNING_ABTEST:
        // when we edit running abtest, we edit from the control - original method so by default we init this form at first
        const editVariant = new ABTestDTO(this.originalMethod, {});
        const editVariantDTO = editVariant.createVariantDTO();
        this.initFormValues(editVariantDTO);
        if (mode === ACTIONS.EDIT_RUNNING_ABTEST) {
          this.currentTab = VARIANT.CONTROL;
        }
        this.editRunningABTestData.control = this.formGroup.value;
        break;
    }
  }

  initFormValues(config) {
    const formConfig = this.formConfig.map((field) => {
      return {
        ...field,
        defaultValue: config[field.fieldName],
      };
    });
    this.initForm(formConfig);
  }

  loadTargetConfig(category: ShippingMethod) {
    this.formConfig = ABTestConfig[category];
  }

  initForm(formConfig: fieldsConfig[], keepFieldsOldValue?: string[]) {
    this.formGroup = this.formBuilder.group(this.initFields(formConfig, keepFieldsOldValue), {
      validators: customDeliveryPromiseValidator(this.currentTab),
    });
    this.initFormArrays(formConfig);
  }

  initFormArrays(formConfig: fieldsConfig[]) {
    const arrayFields = formConfig.filter((form) => form.controlType === 'array');
    arrayFields.forEach((field) => {
      if (field.fieldName === 'additionalPriceRanges') {
        if (Array.isArray(field.defaultValue) && field.defaultValue?.length > 0) {
          field.defaultValue.forEach((value) => {
            this.addPriceRange(value);
          });
        }
      }
    });
  }

  resetForm(exceptFields?: string[]) {
    this.initForm(this.formConfig, exceptFields);
  }

  onHideVariantBToggle(event: boolean) {
    // if (event) {
    //   this.resetForm(['hideVariantB', 'percentTestVariantTraffic', 'appearanceLimit']);
    // }
  }

  initFields(formConfig: fieldsConfig[], keepFieldsOldValue?: string[]) {
    const fields = {};

    formConfig.forEach((field: fieldsConfig) => {
      if (keepFieldsOldValue?.length && keepFieldsOldValue.includes(field.fieldName)) {
        fields[field.fieldName] = this.formGroup.value[field.fieldName];
      } else {
        if (field.controlType === 'array') {
          fields[field.fieldName] = this.formBuilder.array([]);
        } else {
          fields[field.fieldName] = [field.defaultValue, field.validators];
        }
      }
    });
    return fields;
  }

  ngOnDestroy() {}

  onTabChange(event) {
    const prevTestVariantFields = this.testVariantFields;
    if (this.currentTab === VARIANT.TEST) {
      //store current unsaved fields
      this.testVariantFields = this.formGroup.value;
      this.editRunningABTestData.test = this.formGroup.value;
    } else {
      this.editRunningABTestData.control = this.formGroup.value;
    }

    this.currentTab = event;
    this.formGroup.markAsPristine();
    this.formGroup.reset();

    switch (event) {
      case VARIANT.CONTROL: {
        if (this.mode === ACTIONS.EDIT_RUNNING_ABTEST) {
          this.initFormValues(this.editRunningABTestData.control);
          return;
        }

        // preset control values
        const variant = new ABTestDTO(this.originalMethod, {});
        const variantFieldsDTO = variant.createVariantDTO();
        this.initFormValues(variantFieldsDTO);
        break;
      }
      case VARIANT.TEST: {
        if (this.mode === ACTIONS.EDIT_RUNNING_ABTEST) {
          if (!Object.keys(this.editRunningABTestData.test).length) {
            this.initFormValues(this.testVariant.testVariantFields);
            this.editRunningABTestData.test = this.formGroup.value;
          } else {
            this.initFormValues(this.editRunningABTestData.test);
          }
          return;
        }

        if (prevTestVariantFields) {
          // preset unsaved values
          this.initFormValues(prevTestVariantFields);
        } else if (this.mode === ACTIONS.CREATE) {
          // preset default values
          this.initForm(this.formConfig);
        } else if (this.mode === ACTIONS.EDIT || this.mode === ACTIONS.DUPLICATE) {
          // preset saved values
          const testVariantFields = this.testVariant.testVariantFields;
          this.initFormValues(testVariantFields);
        }
      }
    }
  }

  refreshEditRunningABTestState(tab: VARIANT) {
    if (tab === VARIANT.CONTROL) {
      this.editRunningABTestData.control = this.formGroup.value;
    } else {
      this.editRunningABTestData.test = this.formGroup.value;
    }
  }

  onEditRunningABTest() {
    if (!this.originalMethod?.id || !this.testVariant?.id) {
      this.toastService.showToast({ mode: 'danger', message: 'Missing control or variant id' });
      return;
    }

    this.refreshEditRunningABTestState(this.currentTab);

    // ensure test variant fields exists
    if (!Object.keys(this.editRunningABTestData.test).length) {
      this.editRunningABTestData.test = this.testVariant.testVariantFields;
    }

    const payload: any = {
      control: {
        id: this.originalMethod.id,
        ...this.editRunningABTestData.control,
      },
      test: {
        ...this.editRunningABTestData.test,
        id: this.testVariant.id,
        originalVariantId: this.originalMethod.id,
      },
    };

    if (this.selectedAbTestGroup?.id) {
      payload.test.abtestGroupId = this.selectedAbTestGroup.id;
    }
    this.store.dispatch(CheckoutActions.editRunningABTest({ payload }));
  }

  onSave() {
    try {
      const abTestDTO = new ABTestDTO();
      const initialValues = this.formGroup.value;
      const atLeastOneFieldChanged = this.formConfig.every(
        (field) => field.defaultValue == initialValues[field.fieldName],
      );
      if (atLeastOneFieldChanged) {
        this.formGroup.setErrors({ no_values_changed: 'At least one value need to be changed' });
      }
      if (!this.formGroup.invalid) {
        const values = { ...this.formGroup.value };

        values.originalVariantId = this.originalMethod.id;
        if (this.mode === ACTIONS.EDIT) {
          values.testVariantId = this.testVariant.id;
        }
        if (this.selectedAbTestGroup?.id) values.abtestGroupId = this.selectedAbTestGroup.id;

        abTestDTO.normalizeDTOValues(values, this.formConfig);
        if (this.mode === ACTIONS.CREATE || this.mode === ACTIONS.DUPLICATE) {
          this.store.dispatch(CheckoutActions.saveVariantTest({ variant: values }));
        } else {
          this.store.dispatch(CheckoutActions.editVariantTest({ variant: values }));
        }
      }
    } catch (e) {
      if (e?.message) {
        this.toastService.showToast({ mode: 'danger', message: e.message });
      }
    }
  }

  removePriceRangeAt(index: number) {
    this.additionalPriceRangeFormArray.removeAt(index);
  }

  addPriceRange(values) {
    const additionalRate = this.formBuilder.group({
      minOrderPriceRange: new UntypedFormControl(values?.minOrderPriceRange ?? null),
      maxOrderPriceRange: new UntypedFormControl(values?.maxOrderPriceRange ?? null),
      rate: new UntypedFormControl(values?.rate ?? ''),
    });
    this.additionalPriceRangeFormArray.push(additionalRate);
  }

  navigateToCheckoutSettings() {
    this.router.navigate([this.CHECKOUT_SETTINGS_URL]);
    this.dialogRef.close();
  }
  onABTestGroupSelected(selectedABTest?: ABTestGroupDTO) {
    this.selectedAbTestGroup = selectedABTest;
  }

  protected readonly ShippingMethod = ShippingMethod;
}
